import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Shield, Download, Globe, Award, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { generateCertificatePDF } from '../utils/generateCertificatePDF';

const CertificatePage = () => {
  const { certificateId } = useParams();
  const { t } = useLanguage();
  const [certificate, setCertificate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Certificate database - language-aware
  const certificates = useMemo(() => {
    const formatDate = (dateStr) => {
      // Convert YYYY-MM-DD to DD.MM.YYYY
      const [year, month, day] = dateStr.split('-');
      return `${day}.${month}.${year}`;
    };

    return {
      'CO2N-2026-001905': {
        valid: true,
        company: 'truja.de',
        website: 'truja.de',
        issuedDate: '02.03.2024',
        expiryDate: '02.03.2027',
        carbonOffset: t('cert-truja-carbon-offset'),
        certificateId: 'CO2N-2026-001905',
        status: t('cert-page-status-active'),
        verificationCount: 1850
      },
      'CO2N-2024-001': {
        valid: true,
        company: 'TechCorp GmbH',
        website: 'www.techcorp.de',
        issuedDate: formatDate('2024-01-15'),
        expiryDate: formatDate('2025-01-15'),
        carbonOffset: '2.5 ton CO₂',
        certificateId: 'CO2N-2024-001',
        status: t('cert-page-status-active'),
        verificationCount: 1247
      },
      'CO2N-2024-002': {
        valid: true,
        company: 'GreenStart Solutions',
        website: 'www.greenstart.com',
        issuedDate: formatDate('2024-02-20'),
        expiryDate: formatDate('2025-02-20'),
        carbonOffset: '1.8 ton CO₂',
        certificateId: 'CO2N-2024-002',
        status: t('cert-page-status-active'),
        verificationCount: 892
      },
      'CO2N-2024-003': {
        valid: true,
        company: 'EcoTech Industries',
        website: 'www.ecotech.com',
        issuedDate: formatDate('2024-03-10'),
        expiryDate: formatDate('2025-03-10'),
        carbonOffset: '3.2 ton CO₂',
        certificateId: 'CO2N-2024-003',
        status: t('cert-page-status-active'),
        verificationCount: 567
      },
      '555270000666-#748999': {
        valid: true,
        company: 'Neckar Paletten',
        website: 'neckar-paletten.de',
        issuedDate: formatDate('2025-01-02'),
        expiryDate: formatDate('2026-01-02'),
        carbonOffset: t('cert-neckar-carbon-offset'),
        certificateId: '555270000666-#748999',
        status: t('cert-page-status-active'),
        verificationCount: 1250
      },
      'CO2N-2025-ISH-001': {
        valid: true,
        company: 'Insektenschutzhelden',
        website: 'insektenschutzhelden.de',
        issuedDate: '18.03.2025',
        expiryDate: '18.03.2026',
        carbonOffset: t('cert-ish-carbon-offset'),
        certificateId: 'CO2N-2025-ISH-001',
        status: t('cert-page-status-active'),
        verificationCount: 342,
        logoUrl: 'https://www.insektenschutzhelden.de/assets/logo-D796bX3J.png'
      },
      'CO2N-2025-FLY24-ENCZ': {
        valid: true,
        company: 'Enucuzfly',
        website: 'enucuzfly.com',
        issuedDate: '15.11.2024',
        expiryDate: '15.11.2026',
        carbonOffset: t('cert-enucuzfly-carbon-offset'),
        certificateId: 'CO2N-2025-FLY24-ENCZ',
        status: t('cert-page-status-active'),
        verificationCount: 410
      }
    };
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      const cert = certificates[certificateId];
      setCertificate(cert || { valid: false, reason: t('cert-verification-not-found') });
      setIsLoading(false);
    }, 500);
  }, [certificateId, t, certificates]);

  const downloadCertificate = async (certId) => {
    if (certificate && certificate.valid) {
      await generateCertificatePDF(certificate, t);
    }
  };

  if (isLoading) {
    return (
      <section className="min-h-screen bg-neutral-50 py-12 pt-32">
        <div className="container-custom max-w-4xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-neutral-600">{t('cert-verification-verifying')}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!certificate || !certificate.valid) {
    return (
      <section className="min-h-screen bg-neutral-50 py-12 pt-32">
        <div className="container-custom max-w-4xl mx-auto">
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 text-center">
            <h1 className="text-3xl font-bold text-red-800 mb-4">{t('cert-verification-invalid')}</h1>
            <p className="text-lg text-red-600">{certificate?.reason || t('cert-verification-not-found')}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-neutral-50 py-12 pt-32">
      <div className="container-custom max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="bg-primary-100 p-4 rounded-full">
              <Shield className="h-12 w-12 text-primary-600" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-800 mb-4">
            {t('cert-page-title')}
          </h1>
          <div className="flex items-center justify-center gap-2 text-green-600 mb-6">
            <CheckCircle className="h-6 w-6" />
            <span className="text-lg font-semibold">{t('cert-verification-valid')}</span>
          </div>
        </div>

        {/* Certificate Details */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-neutral-50 rounded-lg p-6">
              <label className="text-sm font-medium text-neutral-600 mb-2 block">
                {t('cert-verification-company')}
              </label>
              <p className="text-2xl font-bold text-neutral-800">{certificate.company}</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-6">
              <label className="text-sm font-medium text-neutral-600 mb-2 block">
                {t('cert-verification-website')}
              </label>
              <p className="text-2xl font-bold text-primary-600">{certificate.website}</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-6">
              <label className="text-sm font-medium text-neutral-600 mb-2 block">
                {t('cert-verification-issued-date')}
              </label>
              <p className="text-2xl font-bold text-neutral-800">{certificate.issuedDate}</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-6">
              <label className="text-sm font-medium text-neutral-600 mb-2 block">
                {t('cert-verification-expiry-date')}
              </label>
              <p className="text-2xl font-bold text-neutral-800">{certificate.expiryDate}</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-6">
              <label className="text-sm font-medium text-neutral-600 mb-2 block">
                {t('cert-verification-carbon-offset')}
              </label>
              <p className="text-2xl font-bold text-green-600">{certificate.carbonOffset}</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-6">
              <label className="text-sm font-medium text-neutral-600 mb-2 block">
                {t('cert-verification-certificate-id')}
              </label>
              <p className="text-2xl font-bold text-neutral-800 font-mono">{certificate.certificateId}</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-6">
              <label className="text-sm font-medium text-neutral-600 mb-2 block">
                {t('cert-page-status')}
              </label>
              <p className="text-2xl font-bold text-green-600">{certificate.status}</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-6">
              <label className="text-sm font-medium text-neutral-600 mb-2 block">
                {t('cert-page-verification-count')}
              </label>
              <p className="text-2xl font-bold text-neutral-800">{certificate.verificationCount.toLocaleString()}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-neutral-200">
            <button
              onClick={() => downloadCertificate(certificate.certificateId)}
              className="btn-primary flex items-center justify-center space-x-2"
            >
              <Download className="h-5 w-5" />
              <span>{t('cert-verification-download-pdf')}</span>
            </button>
            <div className="flex items-center space-x-2 text-green-600">
              <Shield className="h-5 w-5" />
              <span className="text-sm">{t('cert-verification-blockchain-verified')}</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-md text-center">
            <div className="flex items-center justify-center mb-3">
              <Globe className="h-8 w-8 text-primary-500" />
            </div>
            <div className="text-2xl font-bold text-neutral-800">15K+</div>
            <div className="text-sm text-neutral-600">{t('cert-page-stats-verified')}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-md text-center">
            <div className="flex items-center justify-center mb-3">
              <Award className="h-8 w-8 text-primary-500" />
            </div>
            <div className="text-2xl font-bold text-neutral-800">99.9%</div>
            <div className="text-sm text-neutral-600">{t('cert-page-stats-accuracy')}</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-md text-center">
            <div className="flex items-center justify-center mb-3">
              <Clock className="h-8 w-8 text-primary-500" />
            </div>
            <div className="text-2xl font-bold text-neutral-800">1.5s</div>
            <div className="text-sm text-neutral-600">{t('cert-page-stats-time')}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificatePage;

